import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuthenticatorSetup } from "../../application/hooks/useAuthenticatorSetup";
import type { QrCode } from "../../domain/entities/QrCode";
import { isSixDigitCode } from "../../domain/utils/otpCode";
import { OtpCodeInput } from "./OtpCodeInput";

interface AuthenticatorSetupStepProps {
  idPrefix: string;
  /** From useAuthenticatorSetup().startAuthenticatorSetup, fetched by the parent. */
  qrCode: QrCode;
  onConfirmed: () => void;
  onBack: () => void;
  /** Defaults to "Back". */
  backLabel?: string;
}

/** Dialog step: scan the QR (or copy the key) and confirm the first code. */
export function AuthenticatorSetupStep({
  idPrefix,
  qrCode,
  onConfirmed,
  onBack,
  backLabel,
}: AuthenticatorSetupStepProps) {
  const { t } = useTranslation("settings");
  const { confirmAuthenticatorSetup, loading } = useAuthenticatorSetup();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const handleCopyKey = async () => {
    if (!qrCode.secret) return;
    await navigator.clipboard.writeText(qrCode.secret);
    setKeyCopied(true);
    window.setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!isSixDigitCode(code)) {
      setCodeError(t("verifyIdentity.codeInvalidFormat"));
      return;
    }
    try {
      await confirmAuthenticatorSetup(code);
      onConfirmed();
    } catch (err) {
      setCodeError(
        err instanceof Error && err.message ? err.message : t("verifyIdentity.verifyError"),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-gray-500">{t("verifyIdentity.setupHint")}</p>
      <div className="flex justify-center">
        <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
          <QRCodeSVG value={qrCode.qr_code_uri} size={150} level="H" />
        </div>
      </div>
      {qrCode.secret && (
        <div className="space-y-1">
          <p className="text-xs text-gray-500">{t("verifyIdentity.manualKeyLabel")}</p>
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <code className="min-w-0 flex-1 break-all text-xs">{qrCode.secret}</code>
            <button
              type="button"
              onClick={() => void handleCopyKey()}
              className="shrink-0 cursor-pointer text-primary"
              aria-label={t("verifyIdentity.copyKey")}
            >
              {keyCopied ? (
                <span className="text-xs">{t("verifyIdentity.copied")}</span>
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      )}
      <OtpCodeInput
        id={`${idPrefix}-setup-code`}
        value={code}
        onChange={(value) => {
          setCode(value);
          setCodeError(null);
        }}
        error={codeError}
        disabled={loading}
      />

      <DialogFooter className="gap-2 sm:gap-2">
        <Button className="cursor-pointer" type="button" variant="outline" onClick={onBack} disabled={loading}>
          {backLabel ?? t("verifyIdentity.back")}
        </Button>
        <Button className="cursor-pointer" type="submit" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("verifyIdentity.verifySetup")}
        </Button>
      </DialogFooter>
    </form>
  );
}
